import type PhotoSwipeLightbox from 'photoswipe/lightbox'
import {
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react'

const DEFAULT_PREVIEW_WIDTH = 1200
const DEFAULT_PREVIEW_HEIGHT = 900

/** A photo the lightbox shows; `createLightboxPreviewItem` builds these. */
export interface LightboxItem {
  id: unknown
  src?: string
  url: string
  /** Historical snapshot dimensions are normalized only at the PhotoSwipe boundary. */
  width?: unknown
  height?: unknown
  w?: unknown
  h?: unknown
  caption?: string
}

/** A link also opens the photo without JavaScript; a button only opens the lightbox. */
export type LightboxTriggerMode = 'anchor' | 'button'

/** What a caller passes to `getTriggerProps`: the trigger's own attributes, and its kind. */
export interface LightboxTriggerOptions extends HTMLAttributes<HTMLElement> {
  mode?: LightboxTriggerMode
  type?: 'button' | 'submit' | 'reset'
}

/** Attributes that make an element open the lightbox at one photo. */
export interface LightboxTriggerProps extends HTMLAttributes<HTMLElement> {
  href?: string
  type?: 'button' | 'submit' | 'reset'
  'data-preview-trigger'?: 'true'
  'data-pswp-width'?: number
  'data-pswp-height'?: number
}

/**
 * An item as PhotoSwipe reads it. PhotoSwipe reads a missing slide as `{}` and a missing
 * dimension as 0, whether it is null or undefined.
 */
interface LightboxSlide {
  id?: unknown
  src?: string
  url?: string
  width?: number
  height?: number
  w?: number
  h?: number
  caption?: string
}

export type GetLightboxTriggerProps = (
  index: number,
  props?: LightboxTriggerOptions
) => LightboxTriggerProps

interface ImageLightboxGalleryProps {
  /** A null entry is a photo with no file; its trigger does nothing. */
  items?: (LightboxItem | null)[]
  className?: string
  trapFocus?: boolean
  returnFocus?: boolean
  onOpenChange?: (isOpen: boolean) => void
  children: (gallery: {
    items: (LightboxItem | null)[]
    getTriggerProps: GetLightboxTriggerProps
  }) => ReactNode
}

function sanitizeGalleryId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '-')
}

function getPreviewDimension(value: unknown, fallback: number) {
  return getPositiveDimension(value) ?? fallback
}

function getPositiveDimension(value: unknown) {
  try {
    const number = Number(value)

    return Number.isFinite(number) && number > 0 ? number : null
  } catch {
    // Historical snapshot metadata may not support numeric coercion.
    return null
  }
}

function getTriggerImageDimensions(trigger: HTMLElement) {
  const image = trigger?.querySelector?.('img')
  const width = getPositiveDimension(image?.naturalWidth)
  const height = getPositiveDimension(image?.naturalHeight)

  if (!width || !height) {
    return null
  }

  return { width, height }
}

function getItemsWithTriggerImageDimensions(
  items: LightboxSlide[],
  index: number,
  trigger: HTMLElement
) {
  const item = items[index]

  if (!item?.url) {
    return null
  }

  const width = getPositiveDimension(item.width ?? item.w)
  const height = getPositiveDimension(item.height ?? item.h)

  if (width && height) {
    return null
  }

  const triggerDimensions = getTriggerImageDimensions(trigger)

  if (!triggerDimensions) {
    return null
  }

  return items.map((currentItem, currentIndex) => {
    if (currentIndex !== index) {
      return currentItem
    }

    const resolvedWidth = width ?? triggerDimensions.width
    const resolvedHeight = height ?? triggerDimensions.height

    return {
      ...currentItem,
      width: resolvedWidth,
      height: resolvedHeight,
      w: resolvedWidth,
      h: resolvedHeight,
    }
  })
}

function normalizeLightboxItem(item: LightboxItem | null): LightboxSlide {
  if (!item || typeof item !== 'object') {
    return {}
  }

  const width = getPositiveDimension(item.width ?? item.w) ?? undefined
  const height = getPositiveDimension(item.height ?? item.h) ?? undefined

  return {
    ...item,
    src: item.src ?? item.url,
    width,
    height,
    w: width,
    h: height,
  }
}

const ImageLightboxGallery = ({
  items = [],
  className = '',
  trapFocus = true,
  returnFocus = true,
  onOpenChange,
  children,
}: ImageLightboxGalleryProps) => {
  const id = useId()
  const galleryId = sanitizeGalleryId(`pswp-gallery-${id}`)
  const lightboxRef = useRef<PhotoSwipeLightbox | null>(null)
  const normalizedItems = items.map(normalizeLightboxItem)
  const itemsRef = useRef(normalizedItems)
  const pendingOpenRef = useRef<{ index: number; trigger: HTMLElement } | null>(null)

  // oxlint-disable-next-line react/refs -- grandfathered
  itemsRef.current = normalizedItems

  useEffect(() => {
    let isCancelled = false
    let lightbox: PhotoSwipeLightbox | null = null
    let isDestroyingForCleanup = false

    if (!items.length) {
      return undefined
    }

    void (async () => {
      const [{ default: PhotoSwipeLightbox }, { default: PhotoSwipeDynamicCaption }] =
        await Promise.all([
          import('photoswipe/lightbox'),
          import('photoswipe-dynamic-caption-plugin'),
        ])

      if (isCancelled) {
        return
      }

      let isOpen = false
      lightbox = new PhotoSwipeLightbox({
        gallery: `#${galleryId}`,
        children: 'a[data-preview-trigger="true"]',
        trapFocus,
        returnFocus,
        pswpModule: () => import('photoswipe'),
      })
      const reportClosed = () => {
        if (isDestroyingForCleanup) {
          return
        }

        if (isOpen) {
          isOpen = false
          onOpenChange?.(false)
        }
      }

      lightbox.on('beforeOpen', () => {
        isOpen = true
        onOpenChange?.(true)
      })
      lightbox.on('close', reportClosed)
      lightbox.on('destroy', reportClosed)

      new PhotoSwipeDynamicCaption(lightbox, { type: 'auto' })
      lightbox.init()
      lightboxRef.current = lightbox

      if (pendingOpenRef.current && Number.isInteger(pendingOpenRef.current.index)) {
        const { index: pendingIndex, trigger } = pendingOpenRef.current
        pendingOpenRef.current = null
        const resolvedItems =
          getItemsWithTriggerImageDimensions(itemsRef.current, pendingIndex, trigger) ??
          itemsRef.current

        lightbox.loadAndOpen(pendingIndex, resolvedItems, null)
      }
    })()

    return () => {
      isCancelled = true
      pendingOpenRef.current = null

      try {
        isDestroyingForCleanup = true
        lightbox?.destroy()
      } catch {
        // Ignore teardown errors from partially initialized lightboxes.
      } finally {
        if (lightboxRef.current === lightbox) {
          lightboxRef.current = null
        }
      }
    }
  }, [galleryId, items.length, onOpenChange, returnFocus, trapFocus])

  const getTriggerProps: GetLightboxTriggerProps = (index, props = {}) => {
    const item = items[index]
    const { mode = 'anchor', onClick, type, ...restProps } = props

    if (!item?.url) {
      return {
        ...restProps,
        ...(mode === 'button' ? { type: type ?? 'button' } : {}),
        ...(onClick ? { onClick } : {}),
      }
    }

    const openFromButton = (event: MouseEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      event.currentTarget?.blur?.()

      if (!lightboxRef.current) {
        pendingOpenRef.current = { index, trigger: event.currentTarget }
        return
      }

      const resolvedItems =
        getItemsWithTriggerImageDimensions(itemsRef.current, index, event.currentTarget) ??
        itemsRef.current

      lightboxRef.current.loadAndOpen(index, resolvedItems, null)
    }

    return {
      ...restProps,
      ...(mode === 'anchor'
        ? {
            href: item.url,
            'data-preview-trigger': 'true',
            'data-pswp-width': getPreviewDimension(item.width, DEFAULT_PREVIEW_WIDTH),
            'data-pswp-height': getPreviewDimension(item.height, DEFAULT_PREVIEW_HEIGHT),
          }
        : {
            type: type ?? 'button',
          }),
      onClick: (event) => {
        onClick?.(event)

        if (event.defaultPrevented) {
          return
        }

        if (mode === 'button') {
          openFromButton(event)
          return
        }

        if (!lightboxRef.current) {
          event.stopPropagation()
          return
        }

        event.preventDefault()
        event.stopPropagation()

        const resolvedItems = getItemsWithTriggerImageDimensions(
          itemsRef.current,
          index,
          event.currentTarget
        )

        if (resolvedItems) {
          lightboxRef.current.loadAndOpen(index, resolvedItems, null)
          return
        }

        lightboxRef.current.loadAndOpen(index, itemsRef.current, null)
      },
    }
  }

  return (
    <div id={galleryId} className={className}>
      {children?.({
        items,
        getTriggerProps,
      })}
    </div>
  )
}

export default ImageLightboxGallery
